/*
 * Person area, membership half (audit Phase 3.3): the Roles and Domains tabs
 * of the Person Detail modal. Replaces wwwroot/js/Person/Role.js and
 * wwwroot/js/Person/Domains.js.
 *
 * Both tabs are inline-entry-row tables; see areas/person-common.ts for the
 * row contract. Domain removal is the one genuinely branchy flow in the
 * area: the Api is asked first which PersonClaims / PersonRoles the removal
 * would orphan, and the dialog composes itself from whichever of
 * (last-domain, primary-handover, dependent-cascade) apply.
 *
 * Defects fixed in the move:
 *  - Person.Domain.DomainRow selected tr.person-domain-row[data-id=N] while
 *    DomainRow.cshtml renders data-domain-id. It only ever matched because
 *    SaveDomain planted a placeholder row spelled the other way first; any
 *    other caller found nothing. One spelling now: data-domain-id.
 *  - Person.Role.SavePersonRole cleared the entry row while the follow-up
 *    fetch for the rendered row was still in flight, so the new row was
 *    inserted relative to a row that had just been emptied.
 *  - The role and domain entry rows collapsed to colspan=3 in tables of 3
 *    and 4 columns respectively.
 */
import { ajaxJson, ajaxSwap } from '../core/ajax';
import { one } from '../core/dom';
import { confirm } from '../core/modal';
import { toast } from '../core/toast';
import {
    checkedIn, currentDomainId, escapeHtml, personId, remove,
    resetEntryRow, setDisabled, setHidden, summaryOf, valueIn,
} from './person-common';

// ---------------------------------------------------------------- Roles

const ROLE_ROW = 'tr.new-person-role-row';

export const Role = {
    /** Swap the server-rendered Application -> Role picker into the entry row. */
    AddPersonRole(): void {
        setHidden(ROLE_ROW, false);
        void ajaxSwap({
            container: ROLE_ROW,
            url: `PersonRole/Person/${personId()}/Create?domainId=${encodeURIComponent(currentDomainId())}`,
            mode: 'replaceWith',
            busy: `${ROLE_ROW} .fa-spinner`,
            alertTitle: 'Failed to initialize the new role membership entry-form',
            onSuccess: () => (one(`${ROLE_ROW} #ApplicationId`) as HTMLSelectElement | null)?.focus(),
        });
    },

    SavePersonRole(): void {
        const row = one(ROLE_ROW);
        const person = personId();
        const roleId = valueIn(row, '#RoleId');
        if (!(Number(person) > 0) || !(Number(roleId) > 0)) {
            toast('danger', 'Invalid role assignment');
            return;
        }
        setDisabled(`${ROLE_ROW} .fa-save, ${ROLE_ROW} .fa-undo`, true);
        setHidden(`${ROLE_ROW} .fa-spinner`, false);
        void ajaxJson({
            url: 'PersonRole',
            method: 'POST',
            data: { personId: person, roleId },
            onSuccess: async () => {
                // Fetch the rendered row and insert it BEFORE the entry row,
                // then collapse the entry row -- in that order. The legacy
                // code collapsed first and raced its own insert.
                if (row) {
                    await ajaxSwap({
                        container: row,
                        url: `PersonRole/Person/${person}/Role/${roleId}`,
                        mode: 'before',
                        alertTitle: 'Failed to render the new role assignment',
                    });
                }
                Role.CancelPersonRole();
                toast('success', 'Role assignment saved');
            },
            onFailure: data => {
                toast('danger', 'Failed to save the role assignment', summaryOf(data));
                return true;
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to save the role assignment', error);
                return true;
            },
            onComplete: () => {
                setDisabled(`${ROLE_ROW} .fa-save, ${ROLE_ROW} .fa-undo`, false);
                setHidden(`${ROLE_ROW} .fa-spinner`, true);
            },
        });
    },

    CancelPersonRole(): void {
        // POST PersonRole answers JSON, so there is no validation sibling.
        resetEntryRow('new-person-role-row', null, 3);
    },

    RemovePersonRole(roleId: number | string): void {
        const person = personId();
        if (!(Number(roleId) > 0) || !(Number(person) > 0)) return;
        const rowSelector = `tr.person-role-row[data-role-id='${roleId}']`;
        const name = one(`${rowSelector} td`)?.textContent?.trim() ?? '';
        void confirm({
            title: 'Remove role assignment',
            message: name ? `Remove the "${name}" role assignment?` : 'Remove role assignment?',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                setDisabled(`${rowSelector} .fa-trash`, true);
                setHidden(`${rowSelector} .fa-spinner`, false);
                void ajaxJson({
                    url: `PersonRole/Person/${person}/Role/${roleId}`,
                    method: 'DELETE',
                    onSuccess: () => {
                        remove(rowSelector);
                        toast('success', 'Role assignment removed');
                    },
                    onFailure: data => {
                        toast('danger', 'Failed to remove the role assignment', summaryOf(data));
                        return true;
                    },
                    onError: (_xhr, _status, error) => {
                        toast('danger', 'Failed to remove the role assignment', error);
                        return true;
                    },
                    onComplete: () => {
                        setDisabled(`${rowSelector} .fa-trash`, false);
                        setHidden(`${rowSelector} .fa-spinner`, true);
                    },
                });
            },
        });
    },

    /** Cascading Application -> Role picker (application 0 = global roles). */
    GetRoles(): void {
        const row = one(ROLE_ROW);
        const appId = valueIn(row, '#ApplicationId') || '0';
        const select = one(`${ROLE_ROW} #RoleId`) as HTMLSelectElement | null;
        const reset = (): void => select?.replaceChildren(new Option('(Select a role)', '0'));

        setDisabled(`${ROLE_ROW} #RoleId, ${ROLE_ROW} .fa-save`, true);
        setHidden(`${ROLE_ROW} .fa-spinner`, false);
        void ajaxJson({
            url: `PersonRole/Application/${appId}/Roles`,
            onSuccess: resp => {
                reset();
                const roles = ((resp as { data?: Array<{ id: number; name: string }> }).data) ?? [];
                for (const role of roles) select?.add(new Option(role.name, String(role.id)));
                Role.RoleChanged();
            },
            onFailure: () => { reset(); Role.RoleChanged(); return true; },
            onError: (_xhr, _status, error) => { toast('danger', 'Error fetching Roles', error); return true; },
            onComplete: () => {
                setDisabled(`${ROLE_ROW} #RoleId`, false);
                setHidden(`${ROLE_ROW} .fa-spinner`, true);
            },
        });
    },

    /** Save stays disabled until a real role is picked. */
    RoleChanged(): void {
        const chosen = Number(valueIn(one(ROLE_ROW), '#RoleId')) > 0;
        setDisabled(`${ROLE_ROW} .fa-save`, !chosen);
        for (const save of Array.from(document.querySelectorAll(`${ROLE_ROW} .fa-save`))) {
            save.classList.toggle('text-muted', !chosen);
        }
    },
};

// ---------------------------------------------------------------- Domains

const DOMAIN_ROW = 'tr.new-person-domain-row';

interface DomainRef { id: number; name: string; url: string }
interface ClaimRef { claimType?: string; claimValue?: string; resourceName?: string }
interface RoleRef { roleName?: string; applicationName?: string }
interface Dependents {
    personClaims?: ClaimRef[];
    personRoles?: RoleRef[];
    remainingDomains?: DomainRef[];
    isPrimary?: boolean;
    wouldBeLast?: boolean;
}

export const Domain = {
    /** Re-render one saved membership row in place. */
    DomainRow(domainId: number | string): void {
        const rowSelector = `tr.person-domain-row[data-domain-id='${domainId}']`;
        void ajaxSwap({
            container: rowSelector,
            url: `PersonDomain/Person/${personId()}/Domain/${domainId}`,
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            alertTitle: 'Failed to render the domain assignment',
        });
    },

    AddDomain(): void {
        setHidden(DOMAIN_ROW, false);
        void ajaxSwap({
            container: DOMAIN_ROW,
            url: `PersonDomain/Person/${personId()}/Add`,
            mode: 'replaceWith',
            busy: `${DOMAIN_ROW} .fa-spinner`,
            alertTitle: 'Failed to initialize the new domain-assignment entry form',
        });
    },

    SaveDomain(): void {
        const row = one(DOMAIN_ROW);
        const person = personId();
        const domainId = valueIn(row, '#DomainId');
        const isDefault = checkedIn(row, '#IsDefault');
        if (!(Number(person) > 0) || !(Number(domainId) > 0)) {
            toast('warning', 'Invalid person or domain - operation canceled');
            return;
        }
        setDisabled(`${DOMAIN_ROW} .fa-save, ${DOMAIN_ROW} .fa-undo`, true);
        setHidden(`${DOMAIN_ROW} .fa-spinner`, false);
        void ajaxJson({
            url: 'PersonDomain',
            method: 'POST',
            data: { personId: person, domainId, isDefault },
            onSuccess: () => {
                // A new primary moves the marker on a row we are not holding,
                // so the whole list has to come back; otherwise insert just
                // the one row.
                if (isDefault) {
                    void Domain.LoadDomainsView();
                    return;
                }
                if (row) {
                    void ajaxSwap({
                        container: row,
                        url: `PersonDomain/Person/${person}/Domain/${domainId}`,
                        mode: 'before',
                        alertTitle: 'Failed to render the domain assignment',
                        onComplete: () => Domain.CancelDomain(),
                    });
                }
                toast('success', 'Domain assignment saved');
            },
            onFailure: data => {
                toast('danger', 'Failed to save the domain assignment', summaryOf(data));
                return true;
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to save the domain assignment', error);
                return true;
            },
            onComplete: () => {
                setDisabled(`${DOMAIN_ROW} .fa-save, ${DOMAIN_ROW} .fa-undo`, false);
                setHidden(`${DOMAIN_ROW} .fa-spinner`, true);
            },
        });
    },

    CancelDomain(): void {
        resetEntryRow('new-person-domain-row', 'person-domain-row-validation', 4);
    },

    RemoveDomain(domainId: number | string): void {
        const person = personId();
        const rowSelector = `tr.person-domain-row[data-domain-id='${domainId}']`;
        const domainName = one(`${rowSelector} .person-domain-name`)?.textContent?.trim() ?? '';
        const domainPhrase = domainName ? `the ${domainName} domain` : 'this domain';

        // Pre-flight: which PersonClaims / PersonRoles would this orphan?
        void ajaxJson({
            url: `PersonDomain/Person/${person}/Domain/${domainId}/Dependents`,
            // GetDependents answers a FLAT envelope -- success sits beside
            // personClaims/personRoles rather than wrapping a data object.
            onSuccess: data => showRemoveConfirm(person, domainId, rowSelector, domainPhrase, data as unknown as Dependents),
            onFailure: () => { toast('danger', 'Failed to inspect domain dependents'); return true; },
            onError: (_xhr, _status, error) => { toast('danger', 'Failed to inspect domain dependents', error); return true; },
        });
    },

    SetPrimaryDomain(domainId: number | string): void {
        const person = personId();
        const rowSelector = `tr.person-domain-row[data-domain-id='${domainId}']`;
        void confirm({
            title: 'Set primary domain',
            message: "Set this domain as the person's primary/home domain?",
            confirmText: 'Set Primary',
            variant: 'primary',
            onConfirm: () => {
                setDisabled(`${rowSelector} .fa-trash, ${rowSelector} .fa-chess-rook`, true);
                setHidden(`${rowSelector} .fa-spinner`, false);
                void ajaxJson({
                    url: `PersonDomain/Person/${person}/Domain/${domainId}/SetPrimary`,
                    method: 'PUT',
                    onSuccess: () => { void Domain.LoadDomainsView(); },
                    onFailure: data => {
                        toast('danger', 'Failed to change the primary-domain designation', summaryOf(data));
                        return true;
                    },
                    onError: (_xhr, _status, error) => {
                        toast('danger', 'Failed to change the primary-domain designation', error);
                        return true;
                    },
                    onComplete: () => {
                        setDisabled(`${rowSelector} .fa-trash, ${rowSelector} .fa-chess-rook`, false);
                        setHidden(`${rowSelector} .fa-spinner`, true);
                    },
                });
            },
        });
    },

    LoadDomainsView(): Promise<void> {
        return ajaxSwap({
            container: 'PersonDomainsContainer',
            url: `PersonDomain/Person/${personId()}`,
            mode: 'replaceWith',
            alertTitle: 'Failed to load domain-assignments list',
        });
    },
};

/**
 * The removal dialog. Each section is appended only when its condition holds,
 * so one dialog covers every combination of last-domain, primary-handover and
 * dependent-cascade. With none of them in play the trash click IS the consent
 * and the delete goes straight through.
 */
function showRemoveConfirm(
    person: string, domainId: number | string, rowSelector: string,
    domainPhrase: string, dependents: Dependents,
): void {
    const claims = dependents.personClaims ?? [];
    const roles = dependents.personRoles ?? [];
    const remaining = dependents.remainingDomains ?? [];
    const hasDependents = claims.length > 0 || roles.length > 0;
    const isPrimary = dependents.isPrimary === true;
    const wouldBeLast = dependents.wouldBeLast === true;

    // One remaining membership picks itself; more than one is the operator's
    // call. The last-domain case clears the primary server-side.
    const autoPrimary = isPrimary && remaining.length === 1 ? remaining[0] : null;
    const needsPicker = isPrimary && remaining.length > 1;

    const doDelete = (force: boolean, newPrimaryDomainId: number | null): void => {
        const query: string[] = [];
        if (force) query.push('force=true');
        if (newPrimaryDomainId !== null) query.push(`newPrimaryDomainId=${encodeURIComponent(newPrimaryDomainId)}`);
        setDisabled(`${rowSelector} .fa-trash`, true);
        setHidden(`${rowSelector} .fa-spinner`, false);
        void ajaxJson({
            url: `PersonDomain/Person/${person}/Domain/${domainId}${query.length ? `?${query.join('&')}` : ''}`,
            method: 'DELETE',
            onSuccess: () => {
                // A moved primary marker or an emptied list means the whole
                // table has to be re-rendered; a plain removal does not.
                if (isPrimary || wouldBeLast) void Domain.LoadDomainsView();
                else remove(rowSelector);
                toast('success', force
                    ? `Domain membership removed and ${claims.length + roles.length} dependent row(s) revoked`
                    : 'Domain assignment removed');
            },
            onFailure: data => {
                toast('danger', 'Failed to remove the domain assignment', summaryOf(data));
                return true;
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to remove the domain assignment', error);
                return true;
            },
            onComplete: () => {
                setDisabled(`${rowSelector} .fa-trash`, false);
                setHidden(`${rowSelector} .fa-spinner`, true);
            },
        });
    };

    if (!hasDependents && !isPrimary && !wouldBeLast) {
        doDelete(false, null);
        return;
    }

    let html = '';
    if (wouldBeLast) {
        html += '<div class="alert alert-warning mb-3">'
            + '<i class="fas fa-exclamation-triangle me-1"></i>'
            + '<strong>This is the only domain this user belongs to.</strong> '
            + 'Removing it leaves them with no domain memberships. Their primary will be cleared and '
            + 'they will not be able to act in any domain until a new membership is added.'
            + '</div>';
    }
    if (autoPrimary) {
        html += '<div class="alert alert-info mb-3">'
            + '<i class="fas fa-chess-rook me-1"></i>'
            + `Removing the current primary domain. <strong>${escapeHtml(autoPrimary.name)}</strong> `
            + 'will become the new primary (only remaining membership).'
            + '</div>';
    }
    if (needsPicker) {
        html += '<div class="alert alert-info mb-2">'
            + '<i class="fas fa-chess-rook me-1"></i>'
            + 'Removing the current primary domain. Select a replacement:'
            + '</div><div class="mb-3"><select class="form-select mcp-new-primary-domain">'
            + remaining.map(d => `<option value="${d.id}">${escapeHtml(d.name)} (${escapeHtml(d.url)})</option>`).join('')
            + '</select></div>';
    }
    if (hasDependents) {
        html += `<p>Removing ${escapeHtml(domainPhrase)} will also revoke <strong>${claims.length}</strong> `
            + `claim row(s) and remove the person from <strong>${roles.length}</strong> role membership(s) tied to that domain.</p>`;
        if (claims.length > 0) {
            html += '<div class="mb-2"><strong>Claims to revoke:</strong><ul class="mb-0">'
                + claims.map(c => `<li>${escapeHtml(c.claimType)}${c.claimValue ? ` = "${escapeHtml(c.claimValue)}"` : ''}`
                    + ` on resource <em>${escapeHtml(c.resourceName)}</em></li>`).join('')
                + '</ul></div>';
        }
        if (roles.length > 0) {
            html += '<div class="mb-2"><strong>Role memberships to revoke:</strong><ul class="mb-0">'
                + roles.map(r => `<li>${escapeHtml(r.roleName)}${r.applicationName ? ` (${escapeHtml(r.applicationName)})` : ''}</li>`).join('')
                + '</ul></div>';
        }
        html += '<p class="mb-0 text-danger">This cannot be undone. Affected users must sign out and back in '
            + 'for revoked claims to drop from issued tokens.</p>';
    } else if (!html) {
        html = `<p>Remove membership from ${escapeHtml(domainPhrase)}?</p>`;
    }

    void confirm({
        title: hasDependents ? 'Remove with cascade?' : 'Remove domain membership',
        html,
        confirmText: hasDependents ? 'Remove all' : 'Remove',
        cancelText: 'Cancel',
        variant: 'danger',
        onConfirm: () => {
            let newPrimary: number | null = null;
            if (autoPrimary) {
                newPrimary = autoPrimary.id;
            } else if (needsPicker) {
                const picked = (one('#McpConfirmModal .mcp-new-primary-domain') as HTMLSelectElement | null)?.value;
                newPrimary = picked ? Number.parseInt(picked, 10) : null;
            }
            doDelete(hasDependents, newPrimary);
        },
    });
}
